import type { ShippingAddress, BillingAddress } from '@/services/orders';
import { isSameAddress } from './constants';

interface AddressCardsProps {
  readonly shipping: ShippingAddress | null;
  readonly billing: BillingAddress | null;
}

export const AddressCards = ({ shipping, billing }: AddressCardsProps) => {
  if (!shipping && !billing) return null;

  const sameAddr = isSameAddress(
    shipping ? { line1: shipping.line1, city: shipping.city, postalCode: shipping.postalCode } : null,
    billing ? { line1: billing.line1, city: billing.city, postalCode: billing.postalCode } : null,
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: '12px' }}>
      {/* Shipping */}
      <div
        style={{
          backgroundColor: 'var(--bg-surface)',
          border: '0.5px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 18px',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-primary)',
            marginBottom: '8px',
          }}
        >
          Shipping address
        </div>
        {shipping ? (
          <AddressBlock address={shipping} phone={shipping.phone} />
        ) : (
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
            No shipping address
          </div>
        )}
      </div>

      {/* Billing */}
      <div
        style={{
          backgroundColor: 'var(--bg-surface)',
          border: '0.5px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 18px',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-primary)',
            marginBottom: '8px',
          }}
        >
          Billing address
        </div>
        {sameAddr && (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--text-tertiary)',
              backgroundColor: 'var(--bg-surface-sunken)',
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '4px',
              marginBottom: '6px',
            }}
          >
            Same as shipping
          </div>
        )}
        {billing ? (
          <AddressBlock address={billing} dimmed={sameAddr} />
        ) : (
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
            No billing address
          </div>
        )}
      </div>
    </div>
  );
};

const AddressBlock = ({
  address,
  phone,
  dimmed,
}: {
  readonly address: { readonly name: string; readonly line1: string; readonly line2: string | null; readonly city: string; readonly state: string; readonly postalCode: string; readonly country: string };
  readonly phone?: string | null;
  readonly dimmed?: boolean;
}) => (
  <div
    style={{
      fontSize: '13px',
      color: dimmed ? 'var(--text-tertiary)' : 'var(--text-secondary)',
      lineHeight: '1.6',
    }}
  >
    {address.name}
    <br />
    {address.line1}
    <br />
    {address.line2 && (
      <>
        {address.line2}
        <br />
      </>
    )}
    {address.city}, {address.state} {address.postalCode}
    <br />
    {address.country}
    {phone && (
      <>
        <br />
        <span style={{ color: 'var(--text-tertiary)' }}>{phone}</span>
      </>
    )}
  </div>
);
